import { useMemo } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { usePartner } from './usePartner';
import { usePartnerDetails } from './usePartnerDetails';

export type UserRoleType = 
  | 'global_user'           // Regular user (not partner, not sub-account)
  | 'cobranded_user'        // Sub-account of co-branded partner
  | 'whitelabel_user'       // Sub-account of white-label partner
  | 'cobranded_partner'     // Co-branded partner
  | 'whitelabel_partner';   // White-label partner

/**
 * Hook to determine the current user's role type
 * This consolidates user type detection logic in one place
 */
export function useUserRole(): UserRoleType | null {
  const { profile } = useAuth();
  const { data: currentPartner, isLoading: partnerLoading } = usePartner();
  const { data: parentPartnerDetails, isLoading: parentLoading } = usePartnerDetails();

  return useMemo(() => {
    // Wait for data to load
    if (partnerLoading || parentLoading) {
      return null;
    }

    // Check if user is a partner_client (sub-account)
    if (profile?.user_type === 'partner_client' && profile?.partner_id) {
      // Determine parent partner type
      if (parentPartnerDetails?.partner_type === 'white_label') {
        return 'whitelabel_user';
      }
      if (parentPartnerDetails?.partner_type === 'co_branded') {
        return 'cobranded_user';
      }
    }
    
    // Check if user is a partner themselves
    if (currentPartner?.partner_type === 'white_label') {
      return 'whitelabel_partner';
    }
    
    if (currentPartner?.partner_type === 'co_branded') {
      return 'cobranded_partner';
    }
    
    // Default: regular global user
    return 'global_user';
  }, [
    profile?.user_type,
    profile?.partner_id,
    parentPartnerDetails?.partner_type,
    currentPartner?.partner_type,
    partnerLoading,
    parentLoading
  ]);
}